import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/StatusBadge';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { Upload, Download, Filter, Loader2, Eye, Search, ChevronLeft, ChevronRight, X, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCE_DATABASE_NONE = '__none__';   // the filter value for rows without a source database
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PINNED_ACTIONS = 'sticky right-0 border-l shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.18)] min-w-[88px] text-center';

export default function LcaImpactFactorsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState([]);
  const [methods, setMethods] = useState([]);
  const [sourceDatabaseFilter, setSourceDatabaseFilter] = useState([]);
  const [sourceDatabases, setSourceDatabases] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [reloadCount, setReloadCount] = useState(0);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    axios.get(`${API_URL}/lca-impact-factors/config`)
      .then(res => setMethods([...new Set(res.data.methods.map(m => m.method))]))
      .catch(() => { /* the method filter just stays empty */ });
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/lca-impact-factors/source-databases`)
      .then(res => setSourceDatabases(res.data.source_databases))
      .catch(() => { /* the source database filter just stays empty */ });
  }, [reloadCount]);

  const methodOptions = useMemo(() => methods.map(m => ({ value: m, label: m })), [methods]);
  const sourceDatabaseOptions = useMemo(
    () => [{ value: SOURCE_DATABASE_NONE, label: 'No source database' }, ...sourceDatabases.map(s => ({ value: s, label: s }))],
    [sourceDatabases]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (debouncedSearch) params.append('search', debouncedSearch);
    methodFilter.forEach(v => params.append('methodology', v));
    sourceDatabaseFilter.forEach(v => params.append('source_database', v));
    params.append('page', String(currentPage));
    params.append('page_size', String(pageSize));
    axios.get(`${API_URL}/lca-impact-factors?${params}`)
      .then(res => {
        setItems(res.data.items);
        setTotal(res.data.total);
        setTotalPages(res.data.total_pages);
      })
      .catch(() => toast.error('Failed to load LCA impact factors'))
      .finally(() => setLoading(false));
  }, [statusFilter, debouncedSearch, methodFilter, sourceDatabaseFilter, currentPage, pageSize, reloadCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch, methodFilter, sourceDatabaseFilter, pageSize]);

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/lca-impact-factors/bulk-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'lca_impact_factors_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      toast.error('Please upload a .xlsx file');
      return;
    }

    setUploading(true);
    setResults(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/lca-impact-factors/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const res = response.data;
      setResults(res);
      if (res.failed.length === 0) {
        toast.success(`Submitted ${res.success.length} impact factor(s) for approval`);
      } else if (res.success.length > 0) {
        toast.warning(`${res.success.length} submitted, ${res.failed.length} failed`);
      } else {
        toast.error(`All ${res.failed.length} row(s) failed. Check the errors below.`);
      }
      if (res.success.length > 0) setReloadCount(c => c + 1);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Upload failed: ${msg}`);
      setResults({ success: [], failed: [{ row: '-', errors: [msg] }], total: 0 });
    } finally {
      setUploading(false);
    }
  };

  const resultsTone = !results ? '' : results.failed.length > 0 && results.success.length === 0
    ? 'border-red-200 bg-red-50'
    : results.failed.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50';

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="lca-impact-factors-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">LCA Impact Factors</h1>
            <p className="text-muted-foreground mt-1">Existing impact factors and the ones submitted through Atlas, with their approval status</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="btn-download-template">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <input type="file" ref={fileInputRef} accept=".xlsx,.xlsm" onChange={handleFileUpload} className="hidden" data-testid="input-upload-excel" />
            <Button className="btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-upload-excel">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload Excel
            </Button>
          </div>
        </div>

        {results && (
          <Card className={resultsTone} data-testid="upload-results">
            <CardContent className="p-4">
              <div className="flex items-start gap-2 text-sm">
                <FileSpreadsheet className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div className="font-semibold">Upload Results — Total: {results.total}</div>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setResults(null)} data-testid="dismiss-upload-results">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-1 space-x-4">
                    <span className="text-green-700">✓ {results.success.length} submitted</span>
                    {results.failed.length > 0 && <span className="text-red-600">✗ {results.failed.length} failed</span>}
                  </div>
                  {results.failed.length > 0 && (
                    <div className="mt-3 max-h-64 overflow-y-auto bg-white rounded border border-red-200 p-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-red-100 text-red-800">
                            <th className="p-1 text-left w-20">Row</th>
                            <th className="p-1 text-left">Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.failed.map((f, idx) => (
                            <tr key={idx} className="border-t border-red-100">
                              <td className="p-1 font-mono">{f.row}</td>
                              <td className="p-1 text-red-700">{(f.errors || []).join('; ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-gray-200">
          <CardContent className="p-4 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by IFID, category, method, source database, created by..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 input-eco"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 input-eco" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-1">Filters:</span>
              <MultiSelectFilter label="Method" options={methodOptions} selected={methodFilter} onSelectionChange={setMethodFilter} testIdPrefix="lca-method-" />
              <MultiSelectFilter label="Source Database" options={sourceDatabaseOptions} selected={sourceDatabaseFilter} onSelectionChange={setSourceDatabaseFilter} testIdPrefix="lca-source-database-" />
              {(methodFilter.length > 0 || sourceDatabaseFilter.length > 0) && (
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => { setMethodFilter([]); setSourceDatabaseFilter([]); }} data-testid="clear-filters-btn">
                  <X className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : total === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No LCA impact factors found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="min-w-[1150px]">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="table-dense min-w-[90px]">IFID</TableHead>
                      <TableHead className="table-dense min-w-[260px]">Category</TableHead>
                      <TableHead className="table-dense min-w-[220px]">Method</TableHead>
                      <TableHead className="table-dense min-w-[160px]">Source Database</TableHead>
                      <TableHead className="table-dense min-w-[80px]">Unit</TableHead>
                      <TableHead className="table-dense min-w-[110px]">Status</TableHead>
                      <TableHead className="table-dense min-w-[200px]">Created By</TableHead>
                      <TableHead className="table-dense min-w-[110px]">Created At</TableHead>
                      <TableHead className={`table-dense ${PINNED_ACTIONS} bg-gray-50`}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id ?? `live-${item.emission_factor_impact_id}`} className="table-dense" data-testid={`lca-row-${item.id ?? `live-${item.emission_factor_impact_id}`}`}>
                        <TableCell className="font-mono text-sm whitespace-nowrap">{item.emission_factor_impact_id ?? 'New'}</TableCell>
                        <TableCell className="text-sm">{[item.category, item.sub_category, item.type, item.sub_type].filter(Boolean).join(' / ') || '—'}</TableCell>
                        <TableCell className="whitespace-nowrap font-medium">{item.methodology}{item.is_update && <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">Update</span>}</TableCell>
                        <TableCell className="whitespace-nowrap">{item.source_database || '—'}</TableCell>
                        <TableCell className="whitespace-nowrap">{item.input_unit || '—'}</TableCell>
                        <TableCell><StatusBadge status={item.status} /></TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{item.user_email || '—'}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</TableCell>
                        <TableCell className={`${PINNED_ACTIONS} bg-white`}>
                          <Button variant="ghost" size="icon" onClick={() => navigate(item.id ? `/lca-impact-factors/${item.id}` : `/lca-impact-factors/live/${item.emission_factor_impact_id}`)} title="View Details" data-testid={`view-lca-${item.id ?? `live-${item.emission_factor_impact_id}`}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {!loading && totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} results
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select value={String(pageSize)} onValueChange={(val) => setPageSize(Number(val))}>
                      <SelectTrigger className="w-[70px] h-8" data-testid="page-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map(size => (
                          <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="prev-page-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-testid="next-page-btn">
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
